import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import ProtectedRoute from './components/ProtectedRoute';
import { PermissionsProvider } from './contexts/PermissionsContext';

// Lazy load all pages
const Login = lazy(() => import('./pages/Login'));
const MainLayout = lazy(() => import('./layouts/MainLayout'));
const Customers = lazy(() => import('./pages/Customers'));
const Bookings = lazy(() => import('./pages/Bookings'));
const Invoices = lazy(() => import('./pages/Invoices'));
const Packages = lazy(() => import('./pages/Packages'));
const Employees = lazy(() => import('./pages/Employees'));
const EmployeeDetails = lazy(() => import('./pages/EmployeeDetails'));
const Inventory = lazy(() => import('./pages/Inventory'));
const Analytics = lazy(() => import('./pages/Analytics'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Progress = lazy(() => import('./pages/Progress'));
const ProgressList = lazy(() => import('./pages/ProgressList'));
const Settings = lazy(() => import('./pages/Settings'));
const ProgressSettings = lazy(() => import('./pages/ProgressSettings'));
const MessageSettings = lazy(() => import('./pages/MessageSettings'));
const MessageLogs = lazy(() => import('./pages/MessageLogs'));
const PhotoLab = lazy(() => import('./pages/PhotoLab'));
const PublicPhotoLab = lazy(() => import('./pages/PublicPhotoLab'));
const ClientBookingView = lazy(() => import('./pages/ClientBookingView'));

// Loading component
const LoadingSpinner = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#FDFCFB' }}>
    <CircularProgress sx={{ color: '#8C7361' }} />
  </Box>
);

function App() {
  return (
    <Router>
      <PermissionsProvider>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/dashboard" element={
              <ProtectedRoute permission="dashboard.view">
                <MainLayout>
                  <Dashboard />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/customers" element={
              <ProtectedRoute permission="customers.view">
                <MainLayout>
                  <Customers />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/bookings" element={
              <ProtectedRoute permission="bookings.view">
                <MainLayout>
                  <Bookings />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/invoices" element={
              <ProtectedRoute permission="invoices.view">
                <MainLayout>
                  <Invoices />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/packages" element={
              <ProtectedRoute permission="packages.view">
                <MainLayout>
                  <Packages />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/employees" element={
              <ProtectedRoute permission="employees.view">
                <MainLayout>
                  <Employees />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/employee-details/:employeeId" element={
              <ProtectedRoute permission="employees.view">
                <MainLayout>
                  <EmployeeDetails />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/inventory" element={
              <ProtectedRoute permission="inventory.view">
                <MainLayout>
                  <Inventory />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/analytics" element={
              <ProtectedRoute permission="analytics.view">
                <MainLayout>
                  <Analytics />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/progress" element={
              <ProtectedRoute permission="progress.view">
                <MainLayout>
                  <ProgressList />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/progress/:id" element={
              <ProtectedRoute permission="progress.view">
                <MainLayout>
                  <Progress />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute permission="settings.view">
                <MainLayout>
                  <Settings />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/settings/progress" element={
              <ProtectedRoute permission="settings.view">
                <MainLayout>
                  <ProgressSettings />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/settings/messages" element={
              <ProtectedRoute permission="settings.manage">
                <MainLayout>
                  <MessageSettings />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/message-logs" element={
              <ProtectedRoute permission="settings.manage">
                <MainLayout>
                  <MessageLogs />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/photo-lab" element={
              <ProtectedRoute>
                <MainLayout>
                  <PhotoLab />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/public/lab/:shareLink" element={<PublicPhotoLab />} />
            <Route path="/client/:uniqueId" element={<ClientBookingView />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </Suspense>
      </PermissionsProvider>
    </Router>
  );
}

export default App;
